/**
 * Avisos entre encargadas y notas del silo (docs/esquema.md §12.2 y §12.4), del lado del teléfono:
 * lo que el catálogo trae más lo que este teléfono escribió o anuló y el catálogo todavía no refleja
 * (la cola y los recibos), como en la proyección de los silos abiertos.
 */

import type { MutacionPendiente } from './produccion';

/** Un aviso tal como baja en `avisos_turno` (§5.5, v6). */
export interface AvisoDelCatalogo {
	id: string;
	texto: string;
	registrado_en: string;
	turno: string;
	persona_id: string;
	dispositivo_id: string;
}

/** Una nota tal como baja en `notas_llenado`. */
export interface NotaDelCatalogo {
	id: string;
	llenado_id: string;
	estacion: string;
	texto: string;
	registrado_en: string;
	persona_id: string | null;
	dispositivo_id: string | null;
}

export interface AvisoVisible {
	id: string;
	texto: string;
	registrado_en: string;
	persona_id: string;
	/** Lo dejó este teléfono: se muestra como «Tu aviso», con «Anular», y nunca se esconde con «Entendido». */
	propio: boolean;
	/** Todavía no llegó al servidor (está en la cola). */
	local: boolean;
}

export interface NotaVisible {
	id: string;
	llenado_id: string;
	estacion: string;
	texto: string;
	registrado_en: string;
	persona_id: string | null;
	propia: boolean;
	local: boolean;
}

/** Las horas que un aviso se muestra, contadas desde que se escribió (no desde que subió: §12.2). */
export const HORAS_DE_UN_AVISO = 24;

function campo(payload: unknown, clave: string): string | null {
	const valor = (payload as Record<string, unknown> | null)?.[clave];
	return typeof valor === 'string' ? valor : null;
}

/** `YYYY-MM-DDTHH:MM` de hace `horas` desde `ahoraMs`, en la hora de la planta (UTC−3). */
function haceHoras(ahoraMs: number, horas: number): string {
	return new Date(ahoraMs - 3 * 3_600_000 - horas * 3_600_000).toISOString().slice(0, 16);
}

/**
 * Los avisos que se muestran arriba del inicio de producción: los del catálogo, más los que este
 * teléfono dejó y todavía no se reflejan, menos los anulados acá, menos los «Entendido» (salvo los
 * propios), escritos en las últimas 24 h. El más nuevo primero.
 */
export function avisosVisibles(
	catalogo: AvisoDelCatalogo[],
	mutaciones: MutacionPendiente[],
	entendidos: ReadonlySet<string>,
	ahoraMs: number,
	dispositivoId: string | null,
): AvisoVisible[] {
	const porId = new Map<string, AvisoVisible>();
	for (const a of catalogo) {
		porId.set(a.id, { id: a.id, texto: a.texto, registrado_en: a.registrado_en, persona_id: a.persona_id, propio: a.dispositivo_id === dispositivoId, local: false });
	}
	const anulados = new Set<string>();
	for (const m of mutaciones) {
		const id = campo(m.payload, 'aviso_id');
		if (id === null) continue;
		if (m.tipo === 'aviso.anular') anulados.add(id);
		if (m.tipo === 'aviso.crear' && !porId.has(id)) {
			porId.set(id, {
				id,
				texto: campo(m.payload, 'texto') ?? '',
				registrado_en: campo(m.payload, 'registrado_en') ?? '',
				persona_id: campo(m.payload, 'persona_id') ?? '',
				propio: true,
				local: m.mutacion_id !== undefined,
			});
		}
	}
	const desde = haceHoras(ahoraMs, HORAS_DE_UN_AVISO);
	return [...porId.values()]
		.filter((a) => !anulados.has(a.id) && a.registrado_en >= desde && (a.propio || !entendidos.has(a.id)))
		.sort((a, b) => b.registrado_en.localeCompare(a.registrado_en) || b.id.localeCompare(a.id));
}

/** Las notas de un silo: las del catálogo más las escritas acá sin reflejar, menos las anuladas acá. La más vieja primero. */
export function notasDelSilo(catalogo: NotaDelCatalogo[], mutaciones: MutacionPendiente[], llenadoId: string, dispositivoId: string | null, estacion: string): NotaVisible[] {
	const porId = new Map<string, NotaVisible>();
	for (const n of catalogo) {
		if (n.llenado_id !== llenadoId) continue;
		porId.set(n.id, { ...n, propia: n.dispositivo_id !== null && n.dispositivo_id === dispositivoId, local: false });
	}
	const anuladas = new Set<string>();
	for (const m of mutaciones) {
		const id = campo(m.payload, 'nota_id');
		if (id === null) continue;
		if (m.tipo === 'nota_llenado.anular') anuladas.add(id);
		if (m.tipo === 'nota_llenado.crear' && campo(m.payload, 'llenado_id') === llenadoId && !porId.has(id)) {
			porId.set(id, {
				id,
				llenado_id: llenadoId,
				estacion,
				texto: campo(m.payload, 'texto') ?? '',
				registrado_en: campo(m.payload, 'registrado_en') ?? '',
				persona_id: campo(m.payload, 'persona_id'),
				propia: true,
				local: m.mutacion_id !== undefined,
			});
		}
	}
	return [...porId.values()].filter((n) => !anuladas.has(n.id)).sort((a, b) => a.registrado_en.localeCompare(b.registrado_en) || a.id.localeCompare(b.id));
}

/** Los ids de `entendidos` que siguen en el catálogo: lo demás (anulado o vencido) se poda. */
export function podarEntendidos(entendidos: readonly string[], catalogo: AvisoDelCatalogo[]): string[] {
	const vigentes = new Set(catalogo.map((a) => a.id));
	return entendidos.filter((id) => vigentes.has(id));
}
